import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { AccreditationSmallSection, HeaderSection } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData } from "@storyblok/react";

interface ServiceProps {
  blok?: HeaderSection & SbBlokData;
  trustsmall?: AccreditationSmallSection & SbBlokData;
}

const Service: React.FC<ServiceProps> = ({ blok, trustsmall }) => {
  const [activeTab, setActiveTab] = useState('nav-services');

  if (!blok) return null;

  const renderAccreditations = () => (
    trustsmall?.accreditations && trustsmall.accreditations.length > 0 && (
      <div className="p-4 d-none d-lg-block">
        <div className="row align-items-center justify-content-center g-3 g-xl-2 g-xxl-3">
          {trustsmall.accreditations.map((accreditation) => (
            <div className="col-auto" key={accreditation._uid}>
              <Image
                src={accreditation?.image?.filename || ""}
                alt={accreditation?.alt_image || ""}
                title={accreditation?.alt_image || ""}
                width={accreditation?.width ? Number(accreditation?.width) : 41}
                height={accreditation?.height ? Number(accreditation?.height) : 41}
                className="img-fluid"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                loading="lazy"
                quality={75}
              />
            </div>
          ))}
        </div>
      </div>
    )
  );

  return (
    <div className="dropdown-menu" aria-labelledby="dropdown-services">
      <div className="container">
        <div className="row g-0">
          {/* Tabs Section */}
          <div className="col-lg-8 col-xl-6 border-start border-end">
            <div className="navbar-title h4 mb-3">
              {blok.title || "Services"}
            </div>
            <ul className="nav nav-tabs border-bottom list-unstyled" role="tablist">
              {blok?.items?.slice(1).map((item) => (
                <li
                  key={item._uid}
                  className="nav-item"
                  role="presentation"
                  onMouseEnter={() => setActiveTab(`nav-${item?.label?.replace(/\s+/g, "-").toLowerCase()}`)}
                >
                  <button
                    className={`dropdown-item nav-link ${
                      activeTab === `nav-${item?.label?.replace(/\s+/g, "-").toLowerCase()}` ? "active" : ""
                    } border-top`}
                    id={`nav-${item?.label?.replace(/\s+/g, "-").toLowerCase()}-tab`}
                    role="tab"
                    aria-controls={`nav-${item?.label?.replace(/\s+/g, "-").toLowerCase()}`}
                    aria-selected={activeTab === `nav-${item?.label?.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {item.label}
                    <i className="fas fa-arrow-right ms-auto ms-xl-2 dropdown-arrow-icon"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Tabs Content Section */}
          <div className="col-lg-16 col-xl-18 tab-content">
            {/* Default Services View */}
            <div
              className={`position-relative tab-pane navbar-sub-menu ${activeTab === 'nav-services' ? 'show active' : 'fade'}`}
              id="nav-services"
              role="tabpanel"
            >
              <div className="row g-0">
                <div className="col-24 col-xl-16 d-flex flex-column">
                  <div className="navbar-title h4 mb-0">{blok.center_top_text}</div>
                  <div className="m-4">
                    <div className="h3">{blok.center_title}</div>
                    <p className="lead navbar-main-text">{blok.center_description}</p>
                  </div>

                  <div className="col-24 small fw-bolder text-center text-muted text-uppercase h-ruled mb-0 mt-auto d-none d-lg-flex">
                    Trusted Cyber Security & Compliance Services
                  </div>
                  {renderAccreditations()}
                </div>

                <div className="d-none d-xl-block col col-lg-8 border-start border-end bg-light">
                  {blok?.items?.[0]?.right_image?.filename && (
                    <div className="navbar-header-img">
                      <Image
                        src={blok.items[0].right_image.filename}
                        alt={blok.items[0].right_image.alt || ""}
                        title={blok.items[0].right_image.title || ""}
                        width={653}
                        height={435}
                        className="img-fluid img-cover"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    </div>
                  )}
                  <div className="m-4">
                    <div className="h3 mb-3">{blok?.items?.[0]?.right_title}</div>
                    <p className="mb-4">{blok?.items?.[0]?.right_description}</p>
                    {blok?.items?.[0]?.right_cta && (
                      <Link
                        href={blok?.items?.[0]?.right_cta_link?.cached_url || "#"}
                        className="btn btn-primary rounded-pill d-block"
                      >
                        {blok?.items?.[0]?.right_cta || "Discover More"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Other Tab Contents */}
            {blok.items?.slice(1).map((item) => (
              <div
                key={item._uid}
                className={`position-relative tab-pane navbar-sub-menu ${
                  activeTab === `nav-${item?.label?.replace(/\s+/g, "-").toLowerCase()}` ? "show active" : "fade"
                }`}
                id={`nav-${item?.label?.replace(/\s+/g, "-").toLowerCase()}`}
                role="tabpanel"
                aria-labelledby={`nav-${item?.label?.replace(/\s+/g, "-").toLowerCase()}-tab`}
              >
                <div className="row g-0">
                  <Link
                    href="#"
                    className="navigation-back-btn text-muted"
                    title="Back"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('nav-services');
                    }}
                  >
                    <i className="fas fa-arrow-left fa-fw"></i>
                  </Link>

                  <div className="col-24 col-xl-16 d-flex flex-column">
                    <div className="navbar-title h4 mb-0">{item.label}</div>
                    <div className="m-4">
                      {item.children && item.children.length > 0 && (
                        <ul className="list-unstyled row navbar-icon-links">
                          {item.children.map((child) => (
                            <li key={child._uid} className="col-24 col-lg-12">
                              <Link
                                href={child.link?.cached_url || "#"}
                                className="d-flex text-decoration-none p-2 mx-n2"
                              >
                                {child.using_icon && (
                                  <i className={`fas fa-${child.icon_class} fa-fw me-2 lh-base`}></i>
                                )}
                                <div>
                                  <span className="h4 d-block mb-1">{child.label}</span>
                                  <span className="small text-muted">{child.icon_description}</span>
                                </div>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="col-24 small fw-bolder text-center text-muted text-uppercase h-ruled mb-0 mt-auto d-none d-lg-flex">
                      Trusted Cyber Security & Compliance Services
                    </div>
                    {renderAccreditations()}
                  </div>

                  <div className="d-none d-xl-block col col-lg-8 border-start border-end bg-light">
                    {item.right_image?.filename && (
                      <div className="navbar-header-img">
                        <Image
                          src={item.right_image.filename}
                          alt={item.right_image.alt || ""}
                          title={item.right_image.title || ""}
                          width={653}
                          height={435}
                          className="img-fluid img-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          loading="lazy"
                          quality={75}
                        />
                      </div>
                    )}
                    <div className="m-4">
                      <div className="h3 mb-3">{item.right_title}</div>
                      <p className="mb-4">{item.right_description}</p>
                      {item.right_cta && (
                        <Link
                          href={item.right_cta_link?.cached_url || "#"}
                          className="btn btn-primary rounded-pill d-block"
                        >
                          {item.right_cta || "Discover More"}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Service;
